import type { QueryClient } from "@tanstack/react-query";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";

import type { CanvasesCanvas, CanvasesCanvasVersion } from "@/api-client";
import { cancelCanvasVersionQueries, canvasKeys, removeCanvasVersionScopedQueries } from "@/hooks/useCanvasData";

type CommitMutation = {
  mutateAsync: (commitMessage: string) => Promise<{ version?: CanvasesCanvasVersion }>;
};
type DraftSpec = CanvasesCanvas["spec"] | null;

async function invalidatePostCommitCaches(
  queryClient: QueryClient,
  organizationId: string,
  canvasId: string,
): Promise<void> {
  // Drop canvas-scoped staged overlays immediately so a re-enter after commit
  // cannot briefly resolve the previous live version id from warm cache.
  queryClient.removeQueries({ queryKey: canvasKeys.stagedCanvasSpec(canvasId) });
  queryClient.removeQueries({ queryKey: canvasKeys.stagedConsole(canvasId) });

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: canvasKeys.detail(organizationId, canvasId), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.versionHistory(canvasId), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.canvasStaging(canvasId), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.stagedCanvasSpec(canvasId), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.stagedConsole(canvasId), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.console(canvasId, undefined), refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: canvasKeys.repositoryFiles(canvasId), refetchType: "all" }),
  ]);
}

async function removeStaleVersionQueriesAfterCommit(
  queryClient: QueryClient,
  canvasId: string,
  previousVersionId: string,
  committedVersionId: string,
): Promise<void> {
  if (!previousVersionId || previousVersionId === committedVersionId) {
    return;
  }

  await cancelCanvasVersionQueries(queryClient, canvasId, previousVersionId);
  removeCanvasVersionScopedQueries(queryClient, canvasId, previousVersionId);
}

function clearDraftSpecsAfterCommit(
  draftCanvasSpecsRef: MutableRefObject<Map<string, DraftSpec>>,
  activeCanvasVersionId: string,
  committedVersionId: string,
) {
  draftCanvasSpecsRef.current.delete(activeCanvasVersionId);
  if (committedVersionId !== activeCanvasVersionId) {
    draftCanvasSpecsRef.current.delete(committedVersionId);
  }
}

async function applyPostCommitCacheUpdates({
  queryClient,
  organizationId,
  canvasId,
  previousVersionId,
  committedVersionId,
}: {
  queryClient: QueryClient;
  organizationId: string;
  canvasId: string;
  previousVersionId: string;
  committedVersionId: string;
}) {
  await removeStaleVersionQueriesAfterCommit(queryClient, canvasId, previousVersionId, committedVersionId);
  await invalidatePostCommitCaches(queryClient, organizationId, canvasId);
}

function stampCommittedCanvasDetailCache(
  queryClient: QueryClient,
  organizationId: string,
  canvasId: string,
  committedVersionId: string,
  committedVersion: CanvasesCanvasVersion | undefined,
) {
  if (!committedVersion?.spec) {
    return;
  }

  // Eagerly stamp the committed graph into the canvas detail cache so Configure
  // re-entry (refetchOnMount: false) cannot flash the pre-commit live spec.
  queryClient.setQueryData<CanvasesCanvas | undefined>(canvasKeys.detail(organizationId, canvasId), (current) => {
    if (!current) {
      return current;
    }
    return {
      ...current,
      spec: committedVersion.spec,
      metadata: {
        ...current.metadata,
        liveVersionId: committedVersionId,
      },
    };
  });
}

export async function executeCommitStaging({
  organizationId,
  canvasId,
  activeCanvasVersionId,
  commitMessage,
  queryClient,
  commitCanvasStagingMutation,
  consoleMutationGenerationRef,
  draftCanvasSpecsRef,
  setDraftCanvasSpec,
  setStagingResetNonce,
  ensureVersionActionDraftReady,
  flushRepositoryFileStaging,
  registerIgnoredCanvasUpdatedEcho,
  onCommittedVersionId,
}: {
  organizationId?: string;
  canvasId?: string;
  activeCanvasVersionId: string;
  commitMessage: string;
  queryClient: QueryClient;
  commitCanvasStagingMutation: CommitMutation;
  consoleMutationGenerationRef: MutableRefObject<number>;
  draftCanvasSpecsRef: MutableRefObject<Map<string, DraftSpec>>;
  setDraftCanvasSpec: Dispatch<SetStateAction<DraftSpec>>;
  setStagingResetNonce: Dispatch<SetStateAction<number>>;
  ensureVersionActionDraftReady: (errorMessage: string) => Promise<boolean>;
  flushRepositoryFileStaging?: () => Promise<void>;
  registerIgnoredCanvasUpdatedEcho?: () => () => void;
  onCommittedVersionId?: (versionId: string) => void;
}): Promise<boolean> {
  await flushRepositoryFileStaging?.();
  const isReady = await ensureVersionActionDraftReady("Unable to prepare staged changes for commit");
  if (!isReady) {
    return false;
  }

  consoleMutationGenerationRef.current += 1;
  const releaseCanvasUpdatedEcho = registerIgnoredCanvasUpdatedEcho?.();
  const previousVersionId = activeCanvasVersionId;
  let committedVersionId = activeCanvasVersionId;
  let committedVersion: CanvasesCanvasVersion | undefined;
  try {
    const response = await commitCanvasStagingMutation.mutateAsync(commitMessage);
    committedVersion = response?.version;
    committedVersionId = committedVersion?.metadata?.id || activeCanvasVersionId;
  } catch (error) {
    releaseCanvasUpdatedEcho?.();
    throw error;
  }

  // Leave edit mode before touching caches so version-scoped hooks (console,
  // files, baselines) stop querying the pre-commit live version id.
  onCommittedVersionId?.(committedVersionId);
  clearDraftSpecsAfterCommit(draftCanvasSpecsRef, activeCanvasVersionId, committedVersionId);
  setDraftCanvasSpec(null);

  if (organizationId && canvasId && committedVersionId) {
    stampCommittedCanvasDetailCache(queryClient, organizationId, canvasId, committedVersionId, committedVersion);
    await applyPostCommitCacheUpdates({
      queryClient,
      organizationId,
      canvasId,
      previousVersionId,
      committedVersionId,
    });
  }

  releaseCanvasUpdatedEcho?.();
  setStagingResetNonce((nonce) => nonce + 1);
  return true;
}
